// F&B Industry Categories
export function getBlogCategories(theme) {
  return [
    { id: 'all', label: 'All News', count: 12, color: theme.palette.primary.main },
    { id: 'news', label: 'Breaking News', count: 5, color: theme.palette.error.main },
    { id: 'trends', label: 'Market Trends', count: 4, color: theme.palette.info.main },
    { id: 'insights', label: 'Industry Insights', count: 2, color: theme.palette.success.main },
    { id: 'success', label: 'Success Stories', count: 1, color: theme.palette.warning.main },
  ];
}

// F&B Industry News and Trends
export function getBlogPosts() {
  return [
    {
      id: 1,
      title: 'Breaking: Major Food Supplier Merger Creates New Market Leader',
      excerpt:
        'Industry giants FreshCo and Quality Foods announce $2.3B merger, promising better pricing and expanded product range for restaurants across the region.',
      content: `Industry giants FreshCo and Quality Foods have announced a landmark $2.3 billion merger that will create the largest food supplier network in the region. This strategic consolidation promises to deliver significant benefits for restaurants, including:

• **Better Pricing**: Economies of scale will allow the merged entity to offer more competitive pricing on bulk orders
• **Expanded Product Range**: Combined inventory will provide access to over 15,000 products from a single supplier
• **Improved Delivery**: Enhanced logistics network will reduce delivery times by an average of 2-3 days
• **Quality Assurance**: Unified quality control standards across all product lines

The merger is expected to be completed by Q3 2024, with full integration of systems and processes completed by year-end. Restaurant owners are advised to review their current supplier contracts and prepare for the transition period.`,
      category: 'news',
      author: 'Industry Reporter',
      authorAvatar: '/assets/images/avatars/avatar-1.jpg',
      publishDate: '2024-01-15',
      readTime: '3 min read',
      image: '/assets/images/supplier-merger.jpg',
      featured: true,
      tags: ['Breaking News', 'Supplier Merger', 'Market Changes'],
    },
    {
      id: 2,
      title: 'Restaurant Industry Alert: New Food Safety Regulations Coming in Q2 2024',
      excerpt:
        "FDA announces stricter food safety standards affecting all restaurants and suppliers. Here's what you need to know to prepare.",
      content: `The FDA has announced comprehensive updates to food safety regulations that will take effect in Q2 2024. These changes represent the most significant update to restaurant food safety standards in over a decade.

**Key Changes Include:**

• **Enhanced Temperature Monitoring**: Real-time temperature tracking required for all cold storage units
• **Digital Record Keeping**: Paper-based logs will no longer be accepted; digital systems mandatory
• **Supplier Verification**: Restaurants must verify supplier food safety certifications quarterly
• **Staff Training**: Mandatory food safety training increased from 2 to 4 hours annually
• **Inspection Frequency**: Health inspections will occur quarterly instead of annually

**Compliance Timeline:**
- Q1 2024: Training and system implementation period
- Q2 2024: New regulations take effect
- Q3 2024: First compliance inspections begin

Restaurant owners should begin preparing now by upgrading their monitoring systems and scheduling staff training sessions.`,
      category: 'news',
      author: 'Regulatory Expert',
      authorAvatar: '/assets/images/avatars/avatar-2.jpg',
      publishDate: '2024-01-12',
      readTime: '5 min read',
      image: '/assets/images/food-safety-regulations.png',
      featured: false,
      tags: ['Regulations', 'Food Safety', 'Compliance'],
    },
    {
      id: 3,
      title: 'Trend Watch: Plant-Based Suppliers See 40% Growth in Restaurant Demand',
      excerpt:
        'Restaurants are increasingly sourcing plant-based ingredients as consumer preferences shift. Top suppliers share their insights.',
      content: `The plant-based food market is experiencing unprecedented growth, with restaurant demand increasing by 40% year-over-year. This surge is driven by changing consumer preferences and the growing popularity of flexitarian diets.

**Market Insights:**

• **Consumer Demand**: 65% of diners now actively seek plant-based options when dining out
• **Menu Integration**: 78% of restaurants have added plant-based dishes in the past year
• **Supplier Growth**: Plant-based suppliers report 40% increase in restaurant orders
• **Price Stability**: Plant-based ingredients now cost only 15-20% more than traditional options

**Top Trending Plant-Based Ingredients:**
1. **Oat Milk**: 120% increase in restaurant orders
2. **Plant-Based Proteins**: 85% increase in demand
3. **Alternative Cheeses**: 60% growth in usage
4. **Plant-Based Eggs**: 45% increase in adoption

**Supplier Recommendations:**
- Stock multiple plant-based alternatives for each traditional ingredient
- Provide clear labeling and certification information
- Offer sample programs for new products
- Maintain consistent quality and availability

Restaurants that embrace this trend early will gain a competitive advantage in attracting health-conscious and environmentally aware customers.`,
      category: 'trends',
      author: 'Market Analyst',
      authorAvatar: '/assets/images/avatars/avatar-3.jpg',
      publishDate: '2024-01-10',
      readTime: '4 min read',
      image: '/assets/images/plant-based-trend.jpeg',
      featured: false,
      tags: ['Plant-Based', 'Market Trends', 'Consumer Demand'],
    },
    {
      id: 4,
      title: 'Supplier Spotlight: Local Farm Network Revolutionizes Fresh Produce Delivery',
      excerpt:
        'How a network of 50+ local farms is changing the game for restaurants with same-day fresh produce delivery.',
      content: `A revolutionary network of 50+ local farms is transforming how restaurants source fresh produce. This innovative collaboration has created a same-day delivery system that's changing the industry landscape.

**Network Benefits:**

• **Same-Day Delivery**: Produce harvested in the morning reaches restaurants by dinner service
• **Freshness Guarantee**: Maximum 8 hours from farm to table
• **Cost Reduction**: 25% lower prices compared to traditional distributors
• **Seasonal Variety**: Access to 200+ different produce items year-round
• **Sustainability**: 90% reduction in transportation emissions

**How It Works:**
1. **Morning Harvest**: Farms harvest produce based on daily restaurant orders
2. **Central Hub**: All produce is collected at a central processing facility
3. **Quality Control**: Each item is inspected and graded
4. **Same-Day Delivery**: Refrigerated trucks deliver to restaurants within 8 hours
5. **Digital Tracking**: Real-time updates on order status and delivery times

**Success Stories:**
- "Fresh Bites" restaurant chain reduced produce waste by 60%
- "Urban Eats" increased their farm-to-table menu items by 300%
- "Green Table" achieved 95% customer satisfaction with produce quality

This model is being replicated in other regions, with plans to expand to 15 major metropolitan areas by 2025.`,
      category: 'insights',
      author: 'Local Business Reporter',
      authorAvatar: '/assets/images/avatars/avatar-4.jpg',
      publishDate: '2024-01-08',
      readTime: '6 min read',
      image: '/assets/images/local-farm-network.jpeg',
      featured: false,
      tags: ['Local Sourcing', 'Fresh Produce', 'Innovation'],
    },
    {
      id: 5,
      title: 'Restaurant Tech Update: AI-Powered Inventory Management Goes Mainstream',
      excerpt:
        'Leading restaurants report 30% reduction in food waste using AI inventory systems. Suppliers are adapting to the new digital landscape.',
      content: `Artificial Intelligence is revolutionizing restaurant inventory management, with early adopters reporting impressive results. AI-powered systems are now mainstream, helping restaurants reduce waste and optimize their supply chains.

**AI System Capabilities:**

• **Predictive Analytics**: Forecast demand with 95% accuracy up to 30 days in advance
• **Automated Ordering**: Generate optimal purchase orders based on historical data and trends
• **Waste Reduction**: Identify and prevent over-ordering, reducing food waste by 30%
• **Cost Optimization**: Save 15-25% on inventory costs through smart purchasing
• **Real-time Monitoring**: Track inventory levels and expiration dates automatically

**Implementation Results:**
- **Fast Casual Chains**: 35% reduction in food waste
- **Fine Dining**: 25% improvement in ingredient freshness
- **Quick Service**: 40% faster inventory turnover
- **Catering**: 50% reduction in last-minute supplier calls

**Supplier Adaptations:**
- Digital ordering platforms with AI integration
- Predictive restocking based on restaurant usage patterns
- Real-time inventory visibility for customers
- Automated delivery scheduling

**Getting Started:**
1. Assess current inventory management processes
2. Choose an AI platform that integrates with existing systems
3. Start with a pilot program on high-volume items
4. Gradually expand to full inventory management
5. Monitor and adjust based on performance data

The future of restaurant inventory management is here, and restaurants that don't adapt risk falling behind their competitors.`,
      category: 'trends',
      author: 'Tech Correspondent',
      authorAvatar: '/assets/images/avatars/avatar-5.jpg',
      publishDate: '2024-01-05',
      readTime: '7 min read',
      image: '/assets/images/ai-inventory.jpg',
      featured: false,
      tags: ['AI Technology', 'Inventory Management', 'Digital Transformation'],
    },
    {
      id: 6,
      title: 'Market Report: Seafood Prices Expected to Rise 15% in Q1 2024',
      excerpt:
        'Climate change and supply chain disruptions are driving up seafood costs. Restaurants are exploring alternative suppliers and menu options.',
      content: `Seafood prices are projected to increase by 15% in Q1 2024, driven by climate change impacts and ongoing supply chain challenges. This significant price hike is forcing restaurants to adapt their menus and sourcing strategies.

**Price Increase Breakdown:**

• **Wild-Caught Fish**: 20% increase due to reduced catches and fuel costs
• **Farmed Seafood**: 12% increase due to feed costs and disease management
• **Shellfish**: 18% increase due to water quality issues and harvesting restrictions
• **Premium Varieties**: 25% increase for high-end species like bluefin tuna and king crab

**Root Causes:**
1. **Climate Change**: Warming oceans affecting fish populations and migration patterns
2. **Supply Chain Issues**: Port congestion and transportation costs remain elevated
3. **Regulatory Changes**: Stricter fishing quotas and environmental protections
4. **Feed Costs**: Aquaculture feed prices up 30% due to grain shortages

**Restaurant Adaptation Strategies:**
- **Menu Diversification**: Adding more land-based protein options
- **Seasonal Seafood**: Focusing on locally available, seasonal varieties
- **Alternative Suppliers**: Exploring new sourcing regions and suppliers
- **Price Adjustments**: Gradual menu price increases to maintain margins
- **Customer Education**: Highlighting the value and sustainability of premium seafood

**Supplier Recommendations:**
- Lock in prices with long-term contracts where possible
- Diversify supplier base to reduce dependency on single sources
- Consider frozen alternatives for cost-sensitive menu items
- Explore sustainable aquaculture options

Restaurants that plan ahead and diversify their protein sources will be better positioned to weather these price increases while maintaining customer satisfaction.`,
      category: 'news',
      author: 'Market Reporter',
      authorAvatar: '/assets/images/avatars/avatar-6.jpg',
      publishDate: '2024-01-03',
      readTime: '4 min read',
      image: '/assets/images/seafood-prices.jpg',
      featured: false,
      tags: ['Market Report', 'Price Changes', 'Supply Chain'],
    },
  ];
}
